/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApolloError,
  FetchResult,
  makeVar,
  MutationHookOptions,
  MutationResult,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  ReactiveVar,
  useReactiveVar,
} from "@apollo/client";

export type QueryHookFn<TData, TVars extends OperationVariables> = (
  options: QueryHookOptions<TData, TVars> &
    ({ variables: TVars; skip?: boolean } | { skip: boolean })
) => QueryResult<TData, TVars>;

export type MutationHookFn<TData, TVars extends OperationVariables> = (
  options?: MutationHookOptions<TData, TVars>
) => [
  (opts: { variables: TVars }) => Promise<FetchResult<TData>>,
  MutationResult<TData>,
];

const stableStringify = (v: unknown): string =>
  typeof v === "object" && v !== null
    ? JSON.stringify(v, Object.keys(v as Record<string, any>).sort())
    : String(v);

const queryVarMap = new WeakMap<
  QueryHookFn<any, any>,
  Map<string, ReactiveVar<any>>
>();

const mutationVarMap = new WeakMap<
  MutationHookFn<any, any>,
  Map<string, ReactiveVar<any>>
>();

export function useCachedQuery<TData, TVars extends OperationVariables>(
  useGen: QueryHookFn<TData, TVars>,
  opts?: QueryHookOptions<TData, TVars> &
    ({ variables: TVars; skip?: boolean } | { skip: boolean })
): { cached: TData | null; dataVar: ReactiveVar<TData | null> } & QueryResult<
  TData,
  TVars
> {
  const { variables, skip, onError, ...restOpts } = opts || { skip: false };

  const varKey = variables ? stableStringify(variables) : "__no_vars__";

  let hookMap = queryVarMap.get(useGen);
  if (!hookMap) {
    hookMap = new Map();
    queryVarMap.set(useGen, hookMap);
  }
  let dataVar = hookMap.get(varKey) as ReactiveVar<TData | null> | undefined;
  if (!dataVar) {
    dataVar = makeVar<TData | null>(null);
    hookMap.set(varKey, dataVar);
  }

  const cached = useReactiveVar<TData | null>(dataVar);

  const result = useGen({
    fetchPolicy: "cache-and-network",
    onCompleted: (d) => d && dataVar!(d),
    onError: onError ?? ((e: ApolloError) => console.error(e)),
    ...restOpts,
    ...(variables !== undefined ? { variables } : { skip }),
  } as QueryHookOptions<TData, TVars> &
    ({ variables: TVars; skip?: boolean } | { skip: boolean }));

  // Prefer reactive `cached` so manual `dataVar()` updates (e.g. LiveKit chat)
  // are visible; `result.data` alone would stay stale after those updates.
  return {
    cached,
    dataVar,
    ...result,
    data: cached ?? result.data ?? undefined,
  };
}

export function useCachedMutation<TData, TVars extends OperationVariables>(
  useGen: MutationHookFn<TData, TVars>,
  opts?: MutationHookOptions<TData, TVars>
): [
  TData | null,
  (variables: TVars) => Promise<TData | null>,
  MutationResult<TData>,
] {
  const { onError, ...restOpts } = opts ?? {};

  let hookMap = mutationVarMap.get(useGen);
  if (!hookMap) {
    hookMap = new Map();
    mutationVarMap.set(useGen, hookMap);
  }

  const [mutate, result] = useGen({ onError, ...restOpts });

  const getVarKey = (vars: TVars) =>
    vars ? stableStringify(vars) : "__no_vars__";

  const getDataVar = (vars: TVars) => {
    const k = getVarKey(vars);
    let v = hookMap!.get(k) as ReactiveVar<TData | null> | undefined;
    if (!v) {
      v = makeVar<TData | null>(null);
      hookMap!.set(k, v);
    }
    return v;
  };

  const cached = result.called ? null : undefined;

  const wrapped = async (vars: TVars): Promise<TData | null> => {
    try {
      const res = await mutate({ variables: vars });
      const data = res.data ?? null;
      if (data) getDataVar(vars)!(data);
      return data;
    } catch (e) {
      if (onError) onError(e as ApolloError);
      else console.error(e);
      return null;
    }
  };

  return [cached ?? null, wrapped, result];
}
